import { useState, useRef, useEffect, useMemo } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../ui/dropdown-menu';
import {
  Plus, Upload, Download, Trash2, Edit2, GripVertical, MoreHorizontal,
  Folder, FolderOpen, ChevronRight, Menu,
  Search, X, ListTree, ChevronsDownUp, ChevronsUpDown, Crosshair, Clock, Star,
} from 'lucide-react';
import { AutomatonRow } from './AutomatonRow';

const RECENT_LIMIT = 5;

export function Sidebar({
  groups, automata, currentAutomaton,
  sidebarOpen, setSidebarOpen, sidebarWidth, setSidebarWidth,
  onNewAutomaton, onLoadAutomaton, onDeleteAutomaton, onDuplicateAutomaton,
  onExportAutomaton, onExportGroup, onSaveAsDocx, onOpenRenameDialog,
  onShowGroupRenameDialog, onShowNewGroupDialog, onShowImportDialog,
  onDeleteGroup,
  onContextMenu,
  onMoveAutomaton, onReorderAutomata, onReorderGroups,
  onTogglePin,
  isReadOnly = false,
}) {
  const [expandedGroups, setExpandedGroups] = useState(() => new Set(groups.map(g => g.group_id)));
  const sidebarRef = useRef(null);
  const dragRef = useRef(null);
  const [isResizing, setIsResizing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Track every group id we've ever seen so re-renders triggered by
  // adding ONE new group don't accidentally re-expand groups the user
  // deliberately collapsed earlier.
  const knownGroupIdsRef = useRef(new Set(groups.map(g => g.group_id)));

  useEffect(() => {
    const known = knownGroupIdsRef.current;
    const newlyAdded = groups.filter(g => !known.has(g.group_id));
    if (newlyAdded.length > 0) {
      setExpandedGroups(prev => {
        const next = new Set(prev);
        newlyAdded.forEach(g => next.add(g.group_id));
        return next;
      });
    }
    groups.forEach(g => known.add(g.group_id));
  }, [groups]);

  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      next.has(groupId) ? next.delete(groupId) : next.add(groupId);
      return next;
    });
  };

  const expandAllGroups = () => setExpandedGroups(new Set(groups.map(g => g.group_id)));
  const collapseAllGroups = () => setExpandedGroups(new Set());
  const collapseAllExceptCurrent = () => {
    const defaultGroupId = groups.find(g => g.order === 0)?.group_id;
    const currentGroupId = currentAutomaton?.group_id || defaultGroupId;
    setExpandedGroups(currentGroupId ? new Set([currentGroupId]) : new Set());
  };

  // Expose expandedGroups for import targeting
  const getFirstExpandedGroupId = () => {
    const found = groups.find(g => expandedGroups.has(g.group_id));
    return found?.group_id || groups[0]?.group_id || null;
  };

  // Sidebar resize
  const latestWidthRef = useRef(sidebarWidth);
  const handleResizeStart = (e) => {
    e.preventDefault();
    setIsResizing(true);
    const startX = e.clientX;
    const startWidth = sidebarWidth;
    const onMove = (me) => {
      const newWidth = Math.max(160, Math.min(500, startWidth + (me.clientX - startX)));
      latestWidthRef.current = newWidth;
      setSidebarWidth(newWidth);
    };
    const onUp = () => {
      setIsResizing(false);
      localStorage.setItem('sidebarWidth', String(latestWidthRef.current));
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  // Drag-and-drop state
  const [dropTarget, setDropTarget] = useState(null); // { groupId, index }

  const handleGroupDragOver = (e, targetGroupId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    const drag = dragRef.current;
    if (!drag) return;
    // Dropping on group header = drop at end of group (for automata) or reorder (for groups)
    if (drag.type === 'automaton') {
      const groupAut = automata
        .filter(a => a.group_id === targetGroupId || (!a.group_id && groups.find(g => g.group_id === targetGroupId)?.order === 0))
        .sort((a, b) => (a.order || 0) - (b.order || 0));
      setDropTarget({ groupId: targetGroupId, index: groupAut.length });
    }
  };

  const handleItemDragOver = (e, targetGroupId, itemIndex) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    const drag = dragRef.current;
    if (!drag || drag.type !== 'automaton') return;
    // Determine if dropping above or below the item
    const rect = e.currentTarget.getBoundingClientRect();
    const midY = rect.top + rect.height / 2;
    const idx = e.clientY < midY ? itemIndex : itemIndex + 1;
    setDropTarget({ groupId: targetGroupId, index: idx });
  };

  const handleDrop = (e, targetGroupId) => {
    e.preventDefault();
    const drag = dragRef.current;
    if (!drag) { setDropTarget(null); return; }

    if (drag.type === 'automaton') {
      const target = dropTarget || { groupId: targetGroupId, index: Infinity };
      if (drag.groupId === target.groupId) {
        // Same group — reorder
        const groupAut = automata
          .filter(a => a.group_id === target.groupId || (!a.group_id && groups.find(g => g.group_id === target.groupId)?.order === 0))
          .sort((a, b) => (a.order || 0) - (b.order || 0));
        const fromIdx = groupAut.findIndex(a => a.automaton_id === drag.id);
        if (fromIdx === -1) { setDropTarget(null); dragRef.current = null; return; }
        const reordered = [...groupAut];
        const [moved] = reordered.splice(fromIdx, 1);
        let toIdx = target.index > fromIdx ? target.index - 1 : target.index;
        toIdx = Math.max(0, Math.min(toIdx, reordered.length));
        reordered.splice(toIdx, 0, moved);
        onReorderAutomata(target.groupId, reordered.map(a => a.automaton_id));
      } else {
        // Cross-group move with position
        onMoveAutomaton(drag.id, target.groupId, target.index);
      }
    }

    if (drag.type === 'group') {
      const fromIdx = groups.findIndex(g => g.group_id === drag.id);
      const toIdx = groups.findIndex(g => g.group_id === targetGroupId);
      if (fromIdx !== -1 && toIdx !== -1 && fromIdx !== toIdx) {
        const reordered = [...groups];
        const [moved] = reordered.splice(fromIdx, 1);
        reordered.splice(toIdx, 0, moved);
        onReorderGroups(reordered.map(g => g.group_id));
      }
    }
    setDropTarget(null);
    dragRef.current = null;
  };

  const handleDragEnd = () => {
    dragRef.current = null;
    setDropTarget(null);
  };

  // ============== SEARCH ==============
  const query = searchQuery.trim().toLowerCase();
  const isSearching = query.length > 0;
  const matchesSearch = (a) => a.name.toLowerCase().includes(query);

  // ============== PINNED / RECENT ==============
  const pinnedAutomata = useMemo(
    () => automata.filter(a => a.pinned).sort((a, b) => a.name.localeCompare(b.name)),
    [automata]
  );
  const recentAutomata = useMemo(
    () => automata
      .filter(a => a.last_opened_at)
      .sort((a, b) => new Date(b.last_opened_at) - new Date(a.last_opened_at))
      .slice(0, RECENT_LIMIT),
    [automata]
  );

  const rowCallbacks = {
    onOpenRenameDialog,
    onDuplicate: onDuplicateAutomaton,
    onExport: onExportAutomaton,
    onSaveAsDocx,
    onDelete: onDeleteAutomaton,
    onTogglePin,
    isReadOnly,
  };

  return (
    <>
      <aside
        ref={sidebarRef}
        className={`${sidebarOpen ? '' : 'w-0'} border-r border-border shrink-0 overflow-hidden transition-all duration-200 relative`}
        style={{ width: sidebarOpen ? `${sidebarWidth}px` : 0 }}
      >
        <div className="h-full flex flex-col" style={{ width: `${sidebarWidth}px` }}>
          <div className="p-3 border-b border-border flex items-center justify-between gap-1">
            <h2 className="text-sm font-bold tracking-tight truncate">Automata</h2>
            <div className="flex items-center gap-0.5">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="p-1 hover:bg-muted rounded transition-colors"
                    title="Group view controls"
                    data-testid="collapse-controls-menu-btn"
                  >
                    <ListTree className="w-4 h-4 text-muted-foreground" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={expandAllGroups} data-testid="expand-all-groups-btn">
                    <ChevronsUpDown className="w-4 h-4 mr-2" /> Expand all
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={collapseAllGroups} data-testid="collapse-all-groups-btn">
                    <ChevronsDownUp className="w-4 h-4 mr-2" /> Collapse all
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={collapseAllExceptCurrent} data-testid="collapse-except-current-btn">
                    <Crosshair className="w-4 h-4 mr-2" /> Collapse all except current
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => !isReadOnly && onShowNewGroupDialog()}
                    disabled={isReadOnly}
                    className="p-1 hover:bg-muted rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                    data-testid="new-group-btn"
                  >
                    <Plus className="w-4 h-4 text-muted-foreground" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>{isReadOnly ? 'Read-only while viewing as another user' : 'New group'}</TooltipContent>
              </Tooltip>
            </div>
          </div>

          {/* Search */}
          <div className="p-2 border-b border-border">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search automata..."
                className="h-8 pl-7 pr-7 text-xs"
                data-testid="sidebar-search-input"
              />
              {isSearching && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 hover:bg-muted rounded"
                  data-testid="sidebar-search-clear-btn"
                >
                  <X className="w-3.5 h-3.5 text-muted-foreground" />
                </button>
              )}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto overflow-x-hidden">
            {/* Pinned & Recent (hidden while actively searching to reduce clutter) */}
            {!isSearching && pinnedAutomata.length > 0 && (
              <div className="p-2 pb-0">
                <div className="flex items-center gap-1.5 px-2 py-1 text-xs font-semibold tracking-wider uppercase text-muted-foreground">
                  <Star className="w-3 h-3" /> Pinned
                </div>
                <div className="space-y-0.5" data-testid="pinned-section">
                  {pinnedAutomata.map(a => (
                    <AutomatonRow
                      key={`pinned-${a.automaton_id}`}
                      automaton={a}
                      isActive={currentAutomaton?.automaton_id === a.automaton_id}
                      isPinned
                      dragEnabled={false}
                      onLoad={() => onLoadAutomaton(a.automaton_id)}
                      onContextMenu={(e) => onContextMenu(e, a.automaton_id)}
                      rowTestIdPrefix="pinned-automaton-item"
                      {...rowCallbacks}
                    />
                  ))}
                </div>
              </div>
            )}

            {!isSearching && recentAutomata.length > 0 && (
              <div className="p-2 pb-0">
                <div className="flex items-center gap-1.5 px-2 py-1 text-xs font-semibold tracking-wider uppercase text-muted-foreground">
                  <Clock className="w-3 h-3" /> Recent
                </div>
                <div className="space-y-0.5" data-testid="recent-section">
                  {recentAutomata.map(a => (
                    <AutomatonRow
                      key={`recent-${a.automaton_id}`}
                      automaton={a}
                      isActive={currentAutomaton?.automaton_id === a.automaton_id}
                      isPinned={!!a.pinned}
                      dragEnabled={false}
                      onLoad={() => onLoadAutomaton(a.automaton_id)}
                      onContextMenu={(e) => onContextMenu(e, a.automaton_id)}
                      rowTestIdPrefix="recent-automaton-item"
                      {...rowCallbacks}
                    />
                  ))}
                </div>
              </div>
            )}

            <div className="p-2 space-y-1 overflow-hidden">
              {isSearching && groups.every(g => automata.filter(a => (a.group_id === g.group_id || (!a.group_id && g.order === 0)) && matchesSearch(a)).length === 0) ? (
                <div className="flex flex-col items-center gap-2 py-8 text-center" data-testid="sidebar-search-empty-state">
                  <p className="text-xs text-muted-foreground">No matching automata found</p>
                  <button
                    onClick={() => setSearchQuery('')}
                    className="text-xs text-primary hover:underline"
                    data-testid="sidebar-search-empty-clear-btn"
                  >
                    Clear search
                  </button>
                </div>
              ) : (
                groups.map((group) => {
                  const allGroupAutomata = automata
                    .filter(a => a.group_id === group.group_id || (!a.group_id && group.order === 0))
                    .sort((a, b) => (a.order || 0) - (b.order || 0));
                  const groupAutomata = isSearching ? allGroupAutomata.filter(matchesSearch) : allGroupAutomata;

                  if (isSearching && groupAutomata.length === 0) return null;

                  const isExpanded = isSearching ? true : expandedGroups.has(group.group_id);

                  return (
                    <div
                      key={group.group_id}
                      draggable={!isSearching && !isReadOnly}
                      onDragStart={(e) => {
                        dragRef.current = { type: 'group', id: group.group_id };
                        e.dataTransfer.effectAllowed = 'move';
                      }}
                      onDragEnd={handleDragEnd}
                      onDragOver={(e) => handleGroupDragOver(e, group.group_id)}
                      onDrop={(e) => handleDrop(e, group.group_id)}
                    >
                      {/* Group header */}
                      <div
                        className="group/grp flex items-center gap-1 px-2 py-1.5 rounded-md hover:bg-muted/50 cursor-pointer select-none text-xs font-medium transition-colors"
                        onContextMenu={(e) => {
                          e.preventDefault();
                          if (!isReadOnly) onShowGroupRenameDialog(group.group_id, group.name);
                        }}
                      >
                        {!isSearching && <GripVertical className={`w-3 h-3 text-muted-foreground/50 shrink-0 ${isReadOnly ? '' : 'cursor-grab'}`} />}
                        <button
                          className="flex items-center gap-1.5 flex-1 min-w-0 text-left"
                          onClick={() => !isSearching && toggleGroup(group.group_id)}
                          data-testid={`group-toggle-${group.group_id}`}
                        >
                          <ChevronRight className={`w-3.5 h-3.5 text-muted-foreground shrink-0 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                          {isExpanded
                            ? <FolderOpen className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                            : <Folder className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                          }
                          <span className="font-semibold text-foreground/90 truncate">{group.name}</span>
                          <span className="text-[10px] font-mono text-muted-foreground ml-auto shrink-0">{groupAutomata.length}</span>
                        </button>
                        {!isSearching && (
                          <>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <button
                                  className="p-0.5 hover:bg-accent rounded shrink-0 opacity-0 group-hover/grp:opacity-100 transition-opacity"
                                  onClick={(e) => e.stopPropagation()}
                                  data-testid={`group-menu-${group.group_id}`}
                                >
                                  <MoreHorizontal className="w-3.5 h-3.5 text-muted-foreground" />
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="start">
                                <DropdownMenuItem disabled={isReadOnly} onClick={() => onShowGroupRenameDialog(group.group_id, group.name)} data-testid={`group-menu-rename-${group.group_id}`}>
                                  <Edit2 className="w-4 h-4 mr-2" /> Rename
                                </DropdownMenuItem>
                                <DropdownMenuItem disabled={isReadOnly} onClick={() => {
                                  onShowImportDialog(group.group_id);
                                }} data-testid={`group-menu-import-${group.group_id}`}>
                                  <Upload className="w-4 h-4 mr-2" /> Import
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => {
                                  onExportGroup(group.group_id, group.name);
                                }} data-testid={`group-menu-export-all-${group.group_id}`}>
                                  <Download className="w-4 h-4 mr-2" /> Export All
                                </DropdownMenuItem>
                                {groups.length > 1 && (
                                  <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                      disabled={isReadOnly}
                                      className="text-destructive focus:text-destructive"
                                      onClick={() => {
                                        if (window.confirm(`Delete group "${group.name}"? Automata will be moved to another group.`)) {
                                          onDeleteGroup(group.group_id);
                                        }
                                      }}
                                      data-testid={`group-menu-delete-${group.group_id}`}
                                    >
                                      <Trash2 className="w-4 h-4 mr-2" /> Delete
                                    </DropdownMenuItem>
                                  </>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                            {isReadOnly ? (
                              <button
                                className="p-0.5 rounded opacity-0 group-hover/grp:opacity-40 transition-opacity shrink-0 cursor-not-allowed"
                                onClick={(e) => e.stopPropagation()}
                                disabled
                                title="Read-only while viewing as another user"
                                data-testid={`new-automaton-in-group-${group.group_id}`}
                              >
                                <Plus className="w-3.5 h-3.5 text-muted-foreground" />
                              </button>
                            ) : (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <button
                                    className="p-0.5 hover:bg-accent rounded opacity-0 group-hover/grp:opacity-100 transition-opacity shrink-0"
                                    onClick={(e) => e.stopPropagation()}
                                    data-testid={`new-automaton-in-group-${group.group_id}`}
                                  >
                                    <Plus className="w-3.5 h-3.5 text-muted-foreground" />
                                  </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="start">
                                  <DropdownMenuItem onClick={() => onNewAutomaton(group.group_id, 'DFA')} data-testid={`new-dfa-in-group-${group.group_id}`}>
                                    DFA / NFA
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => onNewAutomaton(group.group_id, 'PDA')} data-testid={`new-pda-in-group-${group.group_id}`}>
                                    PDA
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => onNewAutomaton(group.group_id, 'TM')} data-testid={`new-tm-in-group-${group.group_id}`}>
                                    Turing Machine
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                          </>
                        )}
                      </div>

                      {/* Group contents */}
                      {isExpanded && (
                        <div className="ml-3 pl-3 border-l border-border/50 space-y-0.5 py-0.5">
                          {groupAutomata.length === 0 ? (
                            <div className="text-xs text-muted-foreground/60 py-2 px-2 italic">No automata</div>
                          ) : (
                            groupAutomata.map((a, itemIdx) => (
                              <AutomatonRow
                                key={a.automaton_id}
                                automaton={a}
                                isActive={currentAutomaton?.automaton_id === a.automaton_id}
                                isPinned={!!a.pinned}
                                dragEnabled={!isSearching && !isReadOnly}
                                dropIndicatorTop={!isSearching && dropTarget && dropTarget.groupId === group.group_id && dropTarget.index === itemIdx}
                                dropIndicatorBottom={!isSearching && dropTarget && dropTarget.groupId === group.group_id && dropTarget.index === itemIdx + 1 && itemIdx === groupAutomata.length - 1}
                                onDragStart={(e) => {
                                  e.stopPropagation();
                                  dragRef.current = { type: 'automaton', id: a.automaton_id, groupId: group.group_id };
                                  e.dataTransfer.effectAllowed = 'move';
                                }}
                                onDragEnd={handleDragEnd}
                                onDragOver={(e) => handleItemDragOver(e, group.group_id, itemIdx)}
                                onDrop={(e) => { e.stopPropagation(); handleDrop(e, group.group_id); }}
                                onLoad={() => onLoadAutomaton(a.automaton_id)}
                                onContextMenu={(e) => onContextMenu(e, a.automaton_id)}
                                rowTestIdPrefix="automaton-item"
                                {...rowCallbacks}
                              />
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>

          <div className="p-2 border-t border-border">
            <Button
              variant="outline"
              className="w-full"
              onClick={() => onShowImportDialog(getFirstExpandedGroupId())}
              disabled={isReadOnly}
              title={isReadOnly ? 'Read-only while viewing as another user' : undefined}
              data-testid="import-btn"
            >
              <Upload className="w-4 h-4 mr-2" /> Import
            </Button>
          </div>
        </div>

        {/* Resize Handle */}
        {sidebarOpen && (
          <div
            className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-primary/50 active:bg-primary transition-colors z-10"
            onMouseDown={handleResizeStart}
            data-testid="sidebar-resize-handle"
          />
        )}
      </aside>

      {/* Toggle Sidebar Button */}
      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="absolute top-1/2 -translate-y-1/2 z-10 bg-background border border-border rounded-r-lg p-1 hover:bg-muted"
        style={{ left: sidebarOpen ? `${sidebarWidth}px` : '0' }}
        data-testid="toggle-sidebar-btn"
      >
        <Menu className="w-4 h-4" />
      </button>
    </>
  );
}
